'use client';

import React from 'react';
import { getCdnUrl } from '@/utils/cdn';
import { motion } from 'framer-motion';

const whatsHref = 'https://api.whatsapp.com/send/?phone=5532998809292&text=Oi%21+Vim+da+p%C3%A1gina+do+Empreendimento+Park+Alterosa.+Gostaria+de+mais+informa%C3%A7%C3%B5es.+Pode+me+ajudar%3F&type=phone_number&app_absent=0';

export const HeroParkDaAlterosa: React.FC = () => {
  return (
    <>
      <section className="relative overflow-hidden bg-[#ff7d2a] px-5 pb-14 pt-28 sm:px-8 lg:px-12 lg:pb-20 lg:pt-32">
        {/* Background Pattern */}
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-40 mix-blend-overlay"
          style={{ backgroundImage: `url(${getCdnUrl('/park-alterosa/brand-bg-1.webp')})` }}
          aria-hidden="true"
        />

        <div className="relative mx-auto max-w-7xl">
          <div className="grid items-center gap-10 lg:grid-cols-[minmax(320px,1fr)_minmax(320px,1.02fr)] lg:gap-14">
            
            {/* Left Column: Content */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              {/* MCMV Badge */}
              <p className="inline-flex items-center gap-2 rounded-full border border-white/35 bg-white/16 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-[#afffd5]" aria-hidden="true" />
                Minha Casa Minha Vida · Caixa
              </p>

              {/* Logo */}
              <img
                src={getCdnUrl('/park-alterosa/logo-white.webp')}
                alt="Park Alterosa"
                className="mt-6.5 h-auto w-[240px] max-w-[80%] object-contain sm:w-[300px]"
              />

              {/* Hash Title */}
              <div className="relative mt-6">
                <h1 className="sr-only">
                  Park da Alterosa - Apartamentos de 2 Quartos em São João del-Rei - MG | Forbli Construtora
                </h1>
                <b className="pointer-events-none absolute -left-3.5 -top-11 select-none text-[104px] font-bold leading-none text-transparent [stroke:rgba(255,255,255,0.35)] [-webkit-text-stroke:2px_rgba(255,255,255,0.35)] sm:-top-12 sm:text-[170px]">
                  #
                </b>
                <h2 className="relative pl-[30px] text-[32px] font-light leading-[1.08] tracking-[-0.01em] text-white sm:pl-[46px] sm:text-[42px] lg:text-[50px]">
                  Abra as <strong className="font-semibold">portas</strong> para <strong className="font-semibold">um novo capítulo!</strong>
                </h2>
              </div>

              {/* Specs Details */}
              <div className="mt-[18px] flex flex-wrap items-center gap-3 text-white sm:gap-4.5">
                <b className="text-[20px] font-bold">2 <small className="text-[13px] font-medium tracking-[0.14em]">QUARTOS</small></b>
                <i className="h-[26px] w-[1px] bg-white/40" />
                <b className="text-[20px] font-bold">40 a 44,5 <small className="text-[13px] font-medium tracking-[0.14em]">M²</small></b>
                <i className="h-[26px] w-[1px] bg-white/40" />
                <span className="text-[16px] font-medium">Recreio das Alterosas · São João del-Rei</span>
              </div>

              {/* Actions & FGTS Info */}
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 lg:gap-7">
                <a
                  href="#contato"
                  className="shrink-0 inline-flex min-h-[52px] sm:min-h-[54px] items-center justify-center rounded-full bg-white px-7 sm:px-8 text-[15px] sm:text-[17px] font-bold text-[#d06019] shadow-[0_16px_40px_rgba(120,45,0,0.28)] transition duration-250 hover:-translate-y-0.5 hover:bg-[#17453a] hover:text-white whitespace-nowrap"
                >
                  Quero conhecer →
                </a>
                <p className="max-w-[230px] text-[13px] leading-[1.4] text-white/85">
                  Use seu FGTS e subsídio de até R$ 55 mil na entrada.
                </p>
              </div>
            </motion.div>

            {/* Right Column: Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
              className="relative mb-7 min-h-[280px] rounded-[36px] shadow-[0_28px_70px_rgba(120,45,0,0.35)] sm:min-h-[420px] lg:mb-0"
            >
              <div className="relative min-h-[340px] w-full overflow-hidden rounded-[36px] sm:min-h-[450px]">
                <img
                  src={getCdnUrl('/park-alterosa/renders/externas/exterior-1.webp')}
                  alt="Perspectiva externa do Park Alterosa"
                  className="absolute inset-0 h-full w-full object-cover object-[62%_center]"
                  fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#17453a]/28 to-transparent" />
              </div>

              {/* Family Circle Badge */}
              <img
                src={getCdnUrl('/casal.webp')}
                alt="Família no Park Alterosa"
                className="absolute -bottom-6 -left-1.5 z-10 h-[132px] w-[132px] rounded-[999px_999px_999px_0] border-[5px] border-white object-cover shadow-[0_20px_50px_rgba(120,45,0,0.3)] sm:-bottom-6 sm:-left-4.5 sm:h-[190px] sm:w-[190px] sm:border-[6px]"
              />
            </motion.div>

          </div>
        </div>
      </section>

      {/* Highlights Bar */}
      <div className="flex flex-wrap items-center justify-center gap-2.5 bg-[#17453a] px-6 py-4 sm:gap-8.5">
        {["Portaria 24h", "Piscina adulto e infantil", "2 áreas gourmet com churrasqueira", "Área kids", "2 elevadores"].map((item) => (
          <p key={item} className="flex items-center gap-2.5 text-[14.5px] font-medium text-[#eaf6ee]">
            <img src={getCdnUrl('/park-alterosa/isotipo-branco.webp')} alt="" className="h-[15px] w-auto object-contain" />
            {item}
          </p>
        ))}
      </div>
    </>
  );
};
